import React, { createContext, useContext, useCallback } from 'react';

interface ToastOptions {
  variant: 'success' | 'error' | 'warning'|'system';
  title: string;
  text: string;
  icon: string;
  duration?: number;
}

interface ToastWithLinkOptions extends ToastOptions {
  linkSelector?: string;
  linkStyles?: Partial<CSSStyleDeclaration>;
  onLinkClick?: (event: Event) => void;
}

interface ToastContextType {
  showToast: (options: ToastOptions) => Promise<void>;
  showToastWithLink: (options: ToastWithLinkOptions) => Promise<void>;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const createToast = useCallback(async (options: ToastOptions) => {
    const existingToasts = document.querySelectorAll('bds-toast[data-validation="true"]');
    for (const toast of Array.from(existingToasts)) {
      toast.remove();
    }
    const newToast = document.createElement('bds-toast');
    newToast.dataset.validation = 'true';
    document.body.appendChild(newToast);
    await newToast.create({
      variant: options.variant,
      actionType: 'icon',
      toastTitle: options.title,
      buttonAction: 'close',
      toastText: options.text,
      duration: options.duration ?? 4,
      icon: options.icon,
    });
    return newToast;
  }, []);

  const showToast = useCallback(async (options: ToastOptions) => {
    await createToast(options);
  }, [createToast]);

  const showToastWithLink = useCallback(async ({
    linkSelector = '.text-link-notification',
    linkStyles,
    onLinkClick,
    ...toastOptions
  }: ToastWithLinkOptions) => {
    const newToast = await createToast(toastOptions);

    // Wait for Shadow DOM to be fully rendered
    setTimeout(() => {
      const typo = newToast.shadowRoot?.querySelector('bds-typo');
      const link = typo?.querySelector(linkSelector) as HTMLAnchorElement;

      if (link) {
        // Apply custom styles if provided
        if (linkStyles) {
          Object.assign(link.style, linkStyles);
        }

        // Attach click event handler if provided
        if (onLinkClick) {
          link.addEventListener('click', onLinkClick);
        }
      }
    }, 100);
  }, [createToast]);

  const value = React.useMemo(() => ({ showToast, showToastWithLink }), [showToast, showToastWithLink]);

  return (
    <ToastContext.Provider value={value}>
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
